import { useConsentStore } from "@/components/analytics/consent";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

const configured = import.meta.env.VITE_META_PIXEL_ID;
const META_PIXEL_ID =
  configured && configured !== "not-set"
    ? configured
    : import.meta.env.VITE_APP_STAGE === "production"
      ? "28586648397618081"
      : "";

type MetaPixelFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  push: (...args: unknown[]) => void;
  version: string;
};

declare global {
  interface Window {
    _fbq?: MetaPixelFunction;
    fbq?: MetaPixelFunction;
  }
}

export function initializeMetaPixel() {
  if (window.fbq || !META_PIXEL_ID) return;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
      return;
    }
    fbq.queue.push(args);
  } as MetaPixelFunction;

  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.push = fbq as unknown as (...args: unknown[]) => void;

  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  fbq("init", META_PIXEL_ID);
  fbq("track", "PageView");
}

export function MetaPixel() {
  const location = useLocation();
  const enabled = useConsentStore((s) => s.consent === "granted");
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || import.meta.env.DEV) return;
    initializeMetaPixel();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) previousPath.current = null;
    if (!enabled || import.meta.env.DEV || !window.fbq) return;

    const path = `${location.pathname}${location.searchStr}${location.hash}`;
    if (previousPath.current === path) return;

    previousPath.current = path;
    window.fbq("track", "PageView");
  }, [enabled, location.hash, location.pathname, location.searchStr]);

  return null;
}
